import { useEffect } from "react"
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom"
import bbApi from "./api"
import { FullLayout, SingleForm } from "./common/layout"
import { ToastWrapper } from "./common/ui"
import { RequireAuth } from "./common/validation"
import {
  HomePage,
  Brands,
  Integrations,
  LoanOfficers,
  LODetails,
  PricingLogs,
  NotFound,
  Login,
  Password,
  BrandDetails,
  Rates,
  Account,
  Engagements,
  EngagementDetails,
  RecentActivity,
  OrganizationDetails,
  ConfigAdmin,
  Stats,
  LeadDetailsPage,
  PasswordReset,
  Conversations,
  Billing,
  BillingOrgInvoices,
  BillingInvoice,
  LoanPrograms,
  LoanProgramDetails,
  DreamRateDetailsPage,
  PricingEngine,
  ChannelLeadsPage,
  NewCustomer,
  AuditLogs
} from "./pages"
import PlatformUsage from "./pages/platform-usage"
import PaymentManagement from "./pages/payment-management"
import {
  BankBilling,
  MyAccountSettings,
  MyLoProfile,
  BankInvoice,
  MyEmailPreferences
} from "./common/ui/account"
import {
  selectIsLoggedIn,
  selectUserDetails,
  logout,
  getMe,
  selectSessionExpired
} from "./redux/slices/auth"
import { useAppDispatch, useAppSelector } from "./common/hooks"
import SessionExpiredModal from "./common/ui/session-expired-modal"

const LoginRoute = () => {
  // Use custom env variable to distinguish environments
  const env = process.env.REACT_APP_ENV

  if (env === "production") {
    window.location.href = "https://app.bankingbridge.com/auth0/login"
    return null
  }

  // In development or staging, use the regular Login component
  return <Login />
}

function App() {
  const dispatch = useAppDispatch()
  const isLoggedIn = useAppSelector(selectIsLoggedIn)
  const userDetails = useAppSelector(selectUserDetails)
  const isSessionExpired = useAppSelector(selectSessionExpired)
  useEffect(() => {
    ;(window as any).BB = {
      bbApi
    }
  }, [])

  useEffect(() => {
    if (isLoggedIn && !userDetails) {
      dispatch(getMe())
    }
  }, [isLoggedIn, userDetails, dispatch])

  useEffect(() => {
    const validateSession = async () => {
      try {
        await dispatch(getMe()).unwrap()
      } catch (error) {
        dispatch(logout())
      }
    }

    if (isLoggedIn && process.env.REACT_APP_ENV !== "production") {
      validateSession()
    }
  }, [dispatch, isLoggedIn])

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/"
          element={
            <RequireAuth>
              <FullLayout />
            </RequireAuth>
          }
        >
          <Route index element={<HomePage />} />
          <Route path="stats" element={<Stats />} />
          {/* "usage" is deprecated, we keep it temporally */}
          <Route path="usage" element={<Stats />} />
          <Route path="brands" element={<Brands />} />
          <Route path="brands/:brandId" element={<BrandDetails />} />
          <Route path="integrations" element={<Integrations />} />
          <Route path="integrations/:id" element={<Integrations />} />
          <Route path="loan-officers" element={<LoanOfficers />} />
          <Route path="loan-officers/:id" element={<LODetails />} />
          <Route path="pricing-log" element={<PricingLogs />} />
          <Route path="pricing-log/:quoteId" element={<PricingLogs />} />
          <Route
            path="pricing-logs"
            element={<Navigate to="/pricing-log" replace />}
          />
          <Route path="rates" element={<Rates />} />
          <Route path="rates/:id" element={<Rates />} />
          <Route path="engagements" element={<Engagements />} />
          <Route path="engagements/:id" element={<EngagementDetails />} />
          <Route path="dream-rate/:id" element={<DreamRateDetailsPage />} />
          <Route path="leads/:id" element={<LeadDetailsPage />} />
          <Route
            path="conversations"
            element={<Navigate to="/conversations/sms" replace />}
          />
          <Route path="conversations/sms/:_id?" element={<Conversations />} />
          <Route
            path="conversations/advisor/:sessionId?"
            element={<Conversations />}
          />
          <Route
            path="conversations/website-chat/:sessionId?"
            element={<Conversations />}
          />
          <Route path="organizations/:orgId">
            <Route index element={<OrganizationDetails />} />
            <Route path="brands/:brandId" element={<BrandDetails />} />
          </Route>
          <Route path="billing">
            <Route index element={<Billing />} />
            <Route path=":org_id" element={<BillingOrgInvoices />} />
            <Route path=":org_id/:_id" element={<BillingInvoice />} />
          </Route>
          <Route path="account" element={<Account />}>
            <Route index element={<MyAccountSettings />} />
            <Route path="lo-profile" element={<MyLoProfile />} />
            <Route path="billing" element={<BankBilling />} />
            <Route path="billing/:_id" element={<BankInvoice />} />
            <Route path="email-preferences" element={<MyEmailPreferences />} />
            <Route path="payment-management" element={<PaymentManagement />} />
            <Route path="usage" element={<PlatformUsage />} />
          </Route>
          <Route path="recent-activity" element={<RecentActivity />} />
          <Route path="config-admin" element={<ConfigAdmin />} />
          <Route path="audit" element={<AuditLogs />} />
          <Route path="pricing-engine" element={<PricingEngine />} />
          <Route path="channel-leads" element={<ChannelLeadsPage />} />
          <Route path="new-customer" element={<NewCustomer />} />
          <Route path="loan-programs">
            <Route index element={<LoanPrograms />} />
            <Route path=":id" element={<LoanProgramDetails />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Route>

        <Route path="user" element={<SingleForm />}>
          <Route path="login" element={<LoginRoute />} />
          <Route path="password" element={<Password />} />
          <Route path="password-reset/:token" element={<PasswordReset />} />
        </Route>
      </Routes>
      <ToastWrapper />
      <SessionExpiredModal isOpen={isSessionExpired} />
    </BrowserRouter>
  )
}

export default App
